import { createRef } from "react";
import WidgetBase from "../common/widgetBase";
import { Converter } from "../../workspace/converter";
import Clipboard from "../../../controls/common/clipboard";

const ESCAPE_MESSAGE_TYPE = "blobhub:html-widget:escape";

// Bridge appended to the (sandboxed, opaque-origin) iframe so an Escape pressed inside the frame is
// forwarded to the parent — the parent cannot read keystrokes from a cross-origin frame. A sandboxed
// allow-scripts frame may still call parent.postMessage. Only Escape is forwarded.
const ESCAPE_BRIDGE =
  '<script>(function(){document.addEventListener("keydown",function(e){' +
  'if(e.key==="Escape"){parent.postMessage({type:"' + ESCAPE_MESSAGE_TYPE + '"},"*");}});})();</script>';

export default class HtmlNode extends WidgetBase {
  constructor(props) {
    super(props);
    this.iframeRef = createRef();
    this.handleEscapeMessage = this.handleEscapeMessage.bind(this);
  }

  componentDidMount() {
    super.componentDidMount();
    window.addEventListener("message", this.handleEscapeMessage);
  }

  componentWillUnmount() {
    window.removeEventListener("message", this.handleEscapeMessage);
    super.componentWillUnmount();
  }

  // Forward an Escape pressed inside the iframe into the parent: blur the frame so focus returns to the
  // app, then re-dispatch a native Escape keydown so it flows through the workspace's normal Esc handling.
  handleEscapeMessage(event) {
    const iframe = this.iframeRef.current;
    if ( !iframe ) return;
    if ( event.source !== iframe.contentWindow ) return;
    if ( !event.data || event.data.type !== ESCAPE_MESSAGE_TYPE ) return;
    iframe.blur();
    const root = document.body || document.documentElement;
    root.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true }));
  }

  getBoundAlias() {
    return Converter.getComponentPortValueData(this.props.data.component, "source");
  }

  getHtml(value) {
    const content = value && value.message && value.message.content;
    if ( !Array.isArray(content) || content.length === 0 ) return null;
    const first = content[0];
    return first && typeof first.text === "string" ? first.text : null;
  }

  getSourceValue() {
    const alias = this.getBoundAlias();
    if ( !alias || !this.context ) return null;
    const value = this.context.getSessionData(alias);
    return value && value.type === "message" ? value : null;
  }

  renderHeaderAlias() {
    const alias = this.getBoundAlias();
    if ( !alias ) return null;
    return <span className="playground-widget-alias">{alias}</span>;
  }

  renderHeaderActions() {
    const value = this.getSourceValue();
    const html = this.getHtml(value);
    if ( typeof html !== "string" ) return null;
    // Reuse the shared header-controls wrapper (same as EnvelopeControls / thread / graph) so the copy
    // button matches the other widgets' compact header styling (.playground-widget-controls .btn).
    return (
      <span className="playground-widget-controls" onClick={(e) => e.stopPropagation()}>
        <Clipboard text={html} />
      </span>
    );
  }

  renderBody() {
    const [value, notice] = this.loadObjectValue(this.props.data.component, "source", "message");
    if ( notice ) return this.renderNotice(notice);
    const html = this.getHtml(value);
    if ( typeof html !== "string" ) {
      return this.renderNotice({ variant: "warning", message: "The bound message has no HTML content to display." });
    }
    const scriptsEnabled =
      Converter.getComponentPortValueData(this.props.data.component, "scripts") !== "disabled";
    const srcDoc = scriptsEnabled ? html + ESCAPE_BRIDGE : html;
    return (
      <div className="playground-html-frame" style={{ position: "relative", width: "100%", height: "100%" }}>
        <iframe
          ref={this.iframeRef}
          title="HTML widget"
          sandbox={scriptsEnabled ? "allow-scripts" : ""}
          srcDoc={srcDoc}
          referrerPolicy="no-referrer"
          style={{ width: "100%", height: "100%", border: 0 }}
        />
        {!this.isSelected() && (
          <div
            className="playground-html-overlay"
            aria-hidden="true"
            style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, cursor: "pointer" }}
          />
        )}
      </div>
    );
  }
}
